import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import tr from 'date-fns/locale/tr';

import {
  getLibraryAppointmentSettings,
  getLibrarySeatAvailability,
  createLibraryAppointment,
  confirmLibraryAppointment,
  getLibraryAppointmentQr,
  getBlockedSlots,
} from '../../api/libraryAppointmentService';

import '../../styles/pages/libraryAppointment.scss';
import 'react-datepicker/dist/react-datepicker.css';

registerLocale('tr', tr);

const LIBRARY_COORDS = Object.freeze({
  lat: 36.583349726154836,
  lng: 36.18281913855207,
});

const initialForm = {
  firstName: '',
  lastName: '',
  identityNumber: '',
  email: '',
  phone: '',
  appointmentDate: null,
  slotStartTime: '',
};

const parseTimeToMinutes = (value = '00:00') => {
  const [hour = '0', minute = '0'] = value.split(':');
  const h = Number(hour);
  const m = Number(minute);
  if (Number.isNaN(h) || Number.isNaN(m)) {
    return 0;
  }
  return h * 60 + m;
};

const formatMinutesToTime = (minutes) => {
  const safe = Math.max(0, minutes);
  const hour = Math.floor(safe / 60);
  const minute = safe % 60;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

const LibraryAppointment = () => {
  const [form, setForm] = useState(initialForm);

  const [settings, setSettings] = useState(null);
  const [qrModeEnabled, setQrModeEnabled] = useState(true);

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [appointment, setAppointment] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationResult, setVerificationResult] = useState('');

  const [qrImage, setQrImage] = useState('');
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState('');

  const [seatAvailability, setSeatAvailability] = useState({
    maxSeats: 0,
    reservedSeats: [],
    loading: false,
    error: '',
  });
  const [selectedSeat, setSelectedSeat] = useState(null);
  const [seatPage, setSeatPage] = useState(0);
  const [seatsPerPage, setSeatsPerPage] = useState(9);
  const [blockedSlots, setBlockedSlots] = useState([]);

  const slotOptions = useMemo(() => {
    if (!settings?.workingDayStartTime || !settings?.workingDayEndTime) {
      return [];
    }

    const startMinutes = parseTimeToMinutes(settings.workingDayStartTime);
    const endMinutes = parseTimeToMinutes(settings.workingDayEndTime);
    const durationRaw = Number(settings.slotDurationMinutes);
    const duration = Number.isFinite(durationRaw) && durationRaw > 0 ? durationRaw : 240;

    if (duration <= 0 || endMinutes <= startMinutes) {
      return [];
    }

    const slots = [];

    let minutes = startMinutes;
    while (minutes < endMinutes) {
      const startLabel = formatMinutesToTime(minutes);

      let nextLimit = minutes + duration;
      let effectiveEnd;
      let isLast = false;

      // If remaining time < duration, extend to end
      if (endMinutes - nextLimit < duration) {
        effectiveEnd = endMinutes;
        isLast = true;
      } else {
        effectiveEnd = Math.min(nextLimit, endMinutes);
      }

      let displayEnd = effectiveEnd;
      if (displayEnd >= 1440) displayEnd = 1439;

      const endLabel = formatMinutesToTime(displayEnd);
      slots.push({ value: startLabel, label: `${startLabel} - ${endLabel}` });

      if (isLast) break;
      minutes = nextLimit;
    }

    return slots;
  }, [settings]);

  // Check if a slot is blocked
  const isSlotBlocked = useCallback((slotStartTime) => {
    if (!blockedSlots || blockedSlots.length === 0) return false;

    return blockedSlots.some(blocked => {
      const blockedStart = blocked.slotStartTime?.substring(0, 5);
      return blockedStart === slotStartTime;
    });
  }, [blockedSlots]);

  // Filter out blocked slots from available options
  const availableSlotOptions = useMemo(() => {
    return slotOptions.filter(slot => !isSlotBlocked(slot.value));
  }, [slotOptions, isSlotBlocked]);

  const hasBlockedSlots = blockedSlots && blockedSlots.length > 0;

  const seatItems = useMemo(() => {
    const maxSeats = seatAvailability.maxSeats || settings?.maxSeatsPerDay || 0;
    if (!maxSeats) return [];
    const reservedSet = new Set(seatAvailability.reservedSeats || []);
    return Array.from({ length: maxSeats }, (_, idx) => {
      const number = idx + 1;
      return {
        number,
        isReserved: reservedSet.has(number),
      };
    });
  }, [seatAvailability.maxSeats, seatAvailability.reservedSeats, settings?.maxSeatsPerDay]);

  const hasAvailableOnPage = useCallback(
    (pageIndex) => {
      if (seatItems.length === 0) return false;
      const start = pageIndex * seatsPerPage;
      const end = start + seatsPerPage;
      return seatItems.slice(start, end).some((seat) => !seat.isReserved);
    },
    [seatItems, seatsPerPage]
  );

  const totalSeatPages = Math.max(1, Math.ceil(seatItems.length / seatsPerPage));
  const pagedSeats = seatItems.slice(
    seatPage * seatsPerPage,
    seatPage * seatsPerPage + seatsPerPage
  );

  const firstAvailablePage = useMemo(() => {
    for (let i = 0; i < totalSeatPages; i += 1) {
      if (hasAvailableOnPage(i)) return i;
    }
    return 0;
  }, [totalSeatPages, hasAvailableOnPage]);

  const availablePageCount = useMemo(() => {
    let count = 0;
    for (let i = 0; i < totalSeatPages; i += 1) {
      if (hasAvailableOnPage(i)) count += 1;
    }
    return Math.max(count, totalSeatPages || 1);
  }, [totalSeatPages, hasAvailableOnPage]);

  const hasAnyAvailableSeat = seatItems.some((seat) => !seat.isReserved);

  const hasPrevAvailablePage = useMemo(() => {
    for (let i = seatPage - 1; i >= 0; i -= 1) {
      if (hasAvailableOnPage(i)) return true;
    }
    return false;
  }, [seatPage, hasAvailableOnPage]);

  const hasNextAvailablePage = useMemo(() => {
    for (let i = seatPage + 1; i < totalSeatPages; i += 1) {
      if (hasAvailableOnPage(i)) return true;
    }
    return false;
  }, [seatPage, totalSeatPages, hasAvailableOnPage]);

  const reservedCount = seatAvailability.reservedSeats?.length || 0;
  const totalSeats = seatAvailability.maxSeats || settings?.maxSeatsPerDay || 0;
  const occupancyPercent = totalSeats ? Math.round((reservedCount * 100) / totalSeats) : 0;

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mq = window.matchMedia('(min-width: 992px)');
    const updateSeatsPerPage = (e) => setSeatsPerPage(e.matches ? 18 : 9);

    updateSeatsPerPage(mq);
    mq.addEventListener('change', updateSeatsPerPage);

    return () => mq.removeEventListener('change', updateSeatsPerPage);
  }, []);

  useEffect(() => {
    if (!slotOptions.length) {
      setForm((prev) => (prev.slotStartTime ? { ...prev, slotStartTime: '' } : prev));
      return;
    }

    setForm((prev) => {
      if (prev.slotStartTime && slotOptions.some((option) => option.value === prev.slotStartTime)) {
        return prev;
      }
      return { ...prev, slotStartTime: slotOptions[0].value };
    });
  }, [slotOptions]);

  const addressLink = useMemo(() => {
    const { lat, lng } = LIBRARY_COORDS;
    const userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const coordinateStr = `${lat},${lng}`;
    const query = encodeURIComponent('Sakarya, Şehit Astsb. Kd. Ütğ. Ali Ermercan Cd., 31300 İskenderun/Hatay');

    if (isIOS) {
      return `http://maps.apple.com/?daddr=${coordinateStr}&q=${query}`;
    }

    return `https://www.google.com/maps/dir/?api=1&destination=${coordinateStr}&destination_place_id=&travelmode=driving`;
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      const response = await getLibraryAppointmentSettings();
      if (response?.isSuccess) {
        const data = response.data || {};
        setSettings({
          ...data,
          workingDayStartTime: data.workingDayStartTime || '08:00',
          workingDayEndTime: data.workingDayEndTime || '18:00',
          slotDurationMinutes: data.slotDurationMinutes ?? 240,
        });
        setQrModeEnabled(data.qrModeEnabled !== false);
      }
    };

    fetchSettings();
  }, []);

  // Load blocked slots when appointment date changes
  useEffect(() => {
    if (!form.appointmentDate) {
      setBlockedSlots([]);
      return;
    }

    const fetchBlockedSlots = async () => {
      try {
        const dateStr = form.appointmentDate.toISOString().split('T')[0];
        const response = await getBlockedSlots(dateStr);
        if (response?.isSuccess && response.data) {
          setBlockedSlots(response.data);
        } else {
          setBlockedSlots([]);
        }
      } catch (err) {
        console.error('Blocked slots fetch error:', err);
        setBlockedSlots([]);
      }
    };

    fetchBlockedSlots();
  }, [form.appointmentDate]);

  useEffect(() => {
    let cancelled = false;

    const fetchQr = async () => {
      if (step !== 3 || !appointment?.appointmentCode) {
        setQrImage('');
        setQrError('');
        setQrLoading(false);
        return;
      }

      setQrLoading(true);
      setQrError('');
      setQrImage('');

      const response = await getLibraryAppointmentQr(appointment.appointmentCode);

      if (cancelled) return;

      if (response?.isSuccess) {
        setQrImage(response.dataUrl);
      } else {
        setQrError(
          response?.resultMessage ||
          'QR kodu alınırken bir problem oluştu. Lütfen daha sonra tekrar deneyiniz.'
        );
      }
      setQrLoading(false);
    };

    fetchQr();

    return () => {
      cancelled = true;
    };
  }, [step, appointment?.appointmentCode]);

  const minSelectableDate = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);

  const maxSelectableDate = useMemo(() => {
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 1); // Only today and tomorrow
    maxDate.setHours(0, 0, 0, 0);
    return maxDate;
  }, []);

  useEffect(() => {
    if (!form.appointmentDate) {
      setForm((prev) => ({ ...prev, appointmentDate: minSelectableDate }));
    }
  }, [form.appointmentDate, minSelectableDate]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phone' || name === 'identityNumber') {
      const digitsOnly = value.replace(/\D/g, '');
      const maxLength = name === 'phone' ? 10 : 11;
      setForm((prev) => ({ ...prev, [name]: digitsOnly.slice(0, maxLength) }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date) => {
    setForm((prev) => ({ ...prev, appointmentDate: date }));
  };

  const formatDateForRequest = (date) => {
    if (!date) return '';
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString().split('T')[0];
  };

  useEffect(() => {
    if (!form.appointmentDate || !form.slotStartTime) {
      setSeatAvailability((prev) => ({
        maxSeats: settings?.maxSeatsPerDay || prev.maxSeats,
        reservedSeats: [],
        loading: false,
        error: '',
      }));
      setSelectedSeat(null);
      setSeatPage(0);
      return;
    }

    let cancelled = false;

    const fetchAvailability = async () => {
      setSeatAvailability((prev) => ({ ...prev, loading: true, error: '' }));

      const response = await getLibrarySeatAvailability({
        appointmentDate: formatDateForRequest(form.appointmentDate),
        slotStartTime: form.slotStartTime,
      });

      if (cancelled) return;

      if (response?.isSuccess && response.data) {
        const maxSeats = response.data.maxSeats || settings?.maxSeatsPerDay || 0;
        const reservedSeats = response.data.reservedSeats || [];

        setSeatAvailability({
          maxSeats,
          reservedSeats,
          loading: false,
          error: '',
        });
        setSeatPage(0);

        setSelectedSeat((prev) => {
          if (prev && prev >= 1 && prev <= maxSeats && !reservedSeats.includes(prev)) {
            return prev;
          }

          const firstAvailable = Array.from({ length: maxSeats }, (_, idx) => idx + 1).find(
            (seat) => !reservedSeats.includes(seat)
          );

          return firstAvailable || null;
        });
      } else {
        setSeatAvailability({
          maxSeats: settings?.maxSeatsPerDay || 0,
          reservedSeats: [],
          loading: false,
          error: response?.resultMessage || 'Masa bilgisi alınamadı.',
        });
        setSelectedSeat(null);
      }
    };

    fetchAvailability();

    return () => {
      cancelled = true;
    };
  }, [form.appointmentDate, form.slotStartTime, settings?.maxSeatsPerDay]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil((seatAvailability.maxSeats || 0) / seatsPerPage));
    if (seatPage >= totalPages) {
      setSeatPage(Math.max(0, totalPages - 1));
    }
  }, [seatAvailability.maxSeats, seatsPerPage, seatPage]);

  useEffect(() => {
    if (seatItems.length === 0) {
      setSeatPage(0);
      return;
    }

    const currentHasAvailable = hasAvailableOnPage(seatPage);
    if (!currentHasAvailable && firstAvailablePage !== seatPage) {
      setSeatPage(firstAvailablePage);
    }
  }, [seatItems, seatPage, firstAvailablePage, hasAvailableOnPage]);

  const validateForm = () => {
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError('Lütfen ad ve soyad bilgilerinizi giriniz.');
      return false;
    }

    if (!/^\d{11}$/.test(form.identityNumber.trim())) {
      setError('T.C. kimlik numarasi 11 haneli olmalidir.');
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setError('Lütfen geçerli bir e-posta adresi giriniz.');
      return false;
    }

    if (form.phone.trim().length !== 10) {
      setError('Telefon numarasi 10 haneli olmalidir.');
      return false;
    }

    if (!form.appointmentDate) {
      setError('Lütfen bir randevu tarihi seçiniz.');
      return false;
    }

    const selectedDate = new Date(form.appointmentDate);
    selectedDate.setHours(0, 0, 0, 0);

    if (selectedDate < minSelectableDate) {
      setError('Randevu tarihi geçmişte olamaz.');
      return false;
    }

    if (!form.slotStartTime) {
      setError('Lütfen bir randevu saati seçiniz.');
      return false;
    }

    if (!selectedSeat) {
      setError('Lütfen bir masa seçiniz.');
      return false;
    }

    if (seatAvailability.reservedSeats.includes(selectedSeat)) {
      setError('Seçtiğiniz masa dolu görünüyor. Lütfen farklı bir masa seçiniz.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setVerificationResult('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const response = await createLibraryAppointment({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        identityNumber: form.identityNumber.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        appointmentDate: formatDateForRequest(form.appointmentDate),
        slotStartTime: form.slotStartTime,
        seatNumber: selectedSeat,
      });

      if (response?.isSuccess) {
        setAppointment(response.data);
        setMessage(
          response.resultMessage ||
          'Randevu talebiniz alındı. Lütfen doğrulama kodunu giriniz.'
        );
        setStep(2);
      } else {
        setError(
          response?.resultMessage ||
          'Randevu oluşturulurken bir hata oluştu. Lütfen tekrar deneyiniz.'
        );
      }
    } catch (err) {
      setError('Randevu oluşturulurken bir hata oluştu. Lütfen tekrar deneyiniz.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = async (e) => {
    e.preventDefault();

    if (!appointment?.appointmentId) {
      setVerificationResult('Randevu bilgisi bulunamadı. Lütfen yeniden deneyin.');
      return;
    }

    if (!verificationCode.trim()) {
      setVerificationResult('Lütfen doğrulama kodunu giriniz.');
      return;
    }

    setLoading(true);
    setVerificationResult('');
    setError('');

    try {
      const response = await confirmLibraryAppointment({
        appointmentId: appointment.appointmentId,
        verificationCode: verificationCode.trim(),
      });

      if (response?.isSuccess) {
        setVerificationResult(response.resultMessage || 'Randevunuz onaylanmistir.');
        setStep(3);
      } else {
        setVerificationResult(
          response?.resultMessage || 'Doğrulama tamamlanamadı. Lütfen kodunuzu kontrol edin.'
        );
      }
    } catch (err) {
      setVerificationResult('Dogrulama sirasinda bir hata olustu.');
    } finally {
      setLoading(false);
    }
  };

  if (settings && !qrModeEnabled) {
    return (
      <div className="library-appointment-page">
        <div className="container">
          <header className="la-header">
            <h1>Kütüphane Randevu Sistemi</h1>
          </header>
          <div className="la-alert la-alert-error" style={{ marginTop: '2rem' }}>
            <p style={{ margin: 0, fontSize: '1.1rem', fontWeight: 500 }}>
              Randevu sistemi şu anda kullanılamıyor. Lütfen daha sonra tekrar deneyiniz.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="library-appointment-page">
      <div className="container">
        <header className="la-header">
          <h1>Kütüphane Randevu Sistemi</h1>
          <p>
            Kütüphanemizde çalışma alanlarından faydalanmak için randevu
            oluşturabilir, e-posta ile gönderilen doğrulama kodu ile
            randevunuzu onaylayabilirsiniz.
          </p>
        </header>

        {error && <div className="la-alert la-alert-error">{error}</div>}
        {message && step === 2 && <div className="la-alert la-alert-info">{message}</div>}

        {step === 1 && (
          <>
            <form className="la-form" onSubmit={handleSubmit}>
              <section className="la-info la-info-inline">
                <p>
                  Randevularımız günlük olarak düzenlenmektedir. Vatandaşlarımız,
                  randevu tarihine karşılık gelen gün içerisinde, kütüphaneye resmî
                  mesai saatleri süresince giriş yapabilirler.
                </p>
                <p style={{ marginTop: '0.75rem' }}>
                  Randevunuzu iptal etmek isterseniz,{' '}
                  <a
                    href="/hizmet-rehberi/kutuphane-randevu-iptal"
                    style={{
                      color: '#dc2626',
                      fontWeight: '600',
                      textDecoration: 'underline',
                      cursor: 'pointer'
                    }}
                  >
                    buraya tıklayarak
                  </a>
                  {' '}TC kimlik numaranız ile randevu bilgilerinize ulaşabilir
                  ve e-posta adresinize gönderilen doğrulama kodu ile iptal işlemini
                  gerçekleştirebilirsiniz.
                </p>
              </section>
              <div className="la-grid">
                <div className="la-field la-field-firstName">
                  <label htmlFor="firstName">Ad *</label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    maxLength={25}
                    value={form.firstName}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="la-field la-field-lastName">
                  <label htmlFor="lastName">Soyad *</label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    maxLength={25}
                    value={form.lastName}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="la-field la-field-identity">
                  <label htmlFor="identityNumber">T.C. Kimlik No *</label>
                  <input
                    id="identityNumber"
                    name="identityNumber"
                    type="text"
                    value={form.identityNumber}
                    onChange={handleChange}
                    maxLength={11}
                    required
                  />
                </div>

                <div className="la-field la-field-contact">
                  <div className="contact-field">
                    <label htmlFor="email">E-Posta *</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      maxLength={50}
                      value={form.email}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="contact-field">
                    <label htmlFor="phone">Telefon *</label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      maxLength={10}
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="5XXXXXXXXX"
                      required
                    />
                  </div>

                  <div className="contact-field contact-field-slot">
                    {availableSlotOptions.length > 0 ? (
                      <>
                        <label htmlFor="slotStartTime">Randevu Saati *</label>
                        <select
                          id="slotStartTime"
                          value={form.slotStartTime}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, slotStartTime: e.target.value }))
                          }
                          required
                        >
                          {availableSlotOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </>
                    ) : hasBlockedSlots ? (
                      <>
                        <label>Randevu Saati *</label>
                        <p className="la-slot-hint" style={{ color: '#dc2626' }}>
                          Seçilen tarihte tüm randevu saatleri kapalıdır. Lütfen farklı bir tarih seçiniz.
                        </p>
                      </>
                    ) : (
                      <>
                        <label>Randevu Saati *</label>
                        <p className="la-slot-hint">Mesai saatleri tanımlanmadı.</p>
                      </>
                    )}
                  </div>
                </div>

                <div className="la-datepicker-section">
                  <span className="la-datepicker-label">Randevu Tarihi *</span>
                  <div className="la-date-buttons">
                    <button
                      type="button"
                      className={`la-date-btn ${form.appointmentDate &&
                        new Date(form.appointmentDate).toDateString() === new Date().toDateString()
                        ? 'active'
                        : ''
                        }`}
                      onClick={() => handleDateChange(new Date())}
                    >
                      <span className="la-date-label">
                        {new Date().toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' })}
                      </span>
                      <span className="la-date-value">
                        {new Date().toLocaleDateString('tr-TR', { weekday: 'long' })}
                      </span>
                    </button>
                    <button
                      type="button"
                      className={`la-date-btn ${form.appointmentDate &&
                        new Date(form.appointmentDate).toDateString() ===
                        new Date(Date.now() + 86400000).toDateString()
                        ? 'active'
                        : ''
                        }`}
                      onClick={() => {
                        const tmr = new Date();
                        tmr.setDate(tmr.getDate() + 1);
                        handleDateChange(tmr);
                      }}
                    >
                      <span className="la-date-label">
                        {new Date(Date.now() + 86400000).toLocaleDateString('tr-TR', {
                          day: 'numeric',
                          month: 'long',
                        })}
                      </span>
                      <span className="la-date-value">
                        {new Date(Date.now() + 86400000).toLocaleDateString('tr-TR', { weekday: 'long' })}
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="la-seat-picker">
                <div className="la-seat-head">
                  <div className="la-seat-title">
                    <label>Masalar</label>
                    <p className="la-seat-hint">
                      Tarih seçimize göre masalar gösterilir. Dolular kırmızı, boşlar beyazdır.
                    </p>
                  </div>
                  <div className="la-seat-meta">
                    <span className="la-seat-occupancy">
                      Doluluk: {reservedCount}/{totalSeats || '-'} ({occupancyPercent}%)
                    </span>
                  </div>
                  <div className="la-seat-nav">
                    <button
                      type="button"
                      className="la-seat-nav-btn"
                      onClick={() => {
                        let target = seatPage - 1;
                        while (target >= 0 && !hasAvailableOnPage(target)) {
                          target -= 1;
                        }
                        setSeatPage(Math.max(0, target));
                      }}
                      disabled={seatItems.length === 0 || !hasPrevAvailablePage || !hasAnyAvailableSeat}
                    >
                      {'<'}
                    </button>
                    <span className="la-seat-page">
                      {Math.min(seatPage + 1, totalSeatPages)}/{availablePageCount}
                    </span>
                    <button
                      type="button"
                      className="la-seat-nav-btn"
                      onClick={() => {
                        let target = seatPage + 1;
                        while (target < totalSeatPages && !hasAvailableOnPage(target)) {
                          target += 1;
                        }
                        setSeatPage(Math.min(totalSeatPages - 1, target));
                      }}
                      disabled={seatItems.length === 0 || !hasNextAvailablePage || !hasAnyAvailableSeat}
                    >
                      {'>'}
                    </button>
                  </div>
                </div>

                {seatAvailability.loading ? (
                  <p className="la-seat-status">Masalar yükleniyor...</p>
                ) : seatAvailability.error ? (
                  <p className="la-seat-status la-seat-status-error">{seatAvailability.error}</p>
                ) : seatItems.length === 0 ? (
                  <p className="la-seat-status">Masa bilgisi için tarih ve saat seçiniz.</p>
                ) : !hasAnyAvailableSeat ? (
                  <p className="la-seat-status la-seat-status-error">
                    Bu tarih ve saat aralığında boş masa bulunmamaktadır. Lütfen farklı bir saat seçiniz.
                  </p>
                ) : (
                  <div className="la-seat-grid">
                    {pagedSeats.map((seat) => {
                      const isSelected = selectedSeat === seat.number;
                      const isReserved = seat.isReserved;

                      return (
                        <button
                          key={seat.number}
                          type="button"
                          className={`la-seat ${isReserved ? 'is-reserved' : 'is-available'} ${isSelected ? 'is-selected' : ''
                            }`}
                          onClick={() => !isReserved && setSelectedSeat(seat.number)}
                          disabled={isReserved}
                        >
                          <span className="la-seat-number">{seat.number}</span>
                          <span className="la-seat-state">{isReserved ? 'Dolu' : 'Boş'}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <button type="submit" className="la-submit" disabled={loading}>
                {loading ? 'Gönderiliyor…' : 'Randevu Talep Et'}
              </button>
            </form>

            <div className="la-location-only">
              <button
                type="button"
                className="la-location-link"
                onClick={() => window.open(addressLink, '_blank')}
              >
                <span className="la-location-label">Adres</span>
                <span className="la-location-address">
                  Sakarya, Şehit Astsb. Kd. Ütğ. Ali Ermercan Cd., 31300 İskenderun/Hatay
                </span>
              </button>
            </div>
          </>
        )}

        {step === 2 && appointment && (
          <form className="la-form la-form-verify" onSubmit={handleVerification}>
            <div className="la-summary">
              <h3>Randevu Özeti</h3>
              <p>
                <strong>Ad Soyad:</strong> {appointment.fullName}
              </p>
              <p>
                <strong>Tarih:</strong>{' '}
                {new Date(appointment.appointmentDate).toLocaleDateString('tr-TR')}
              </p>
              <p>
                <strong>Saat:</strong>{' '}
                {appointment.slotStartTime && appointment.slotEndTime
                  ? `${appointment.slotStartTime} - ${appointment.slotEndTime}`
                  : '-'}
              </p>
              <p>
                <strong>Masa:</strong> {appointment.seatNumber}
              </p>
              <p>Doğrulama kodu e-posta adresinize gönderildi.</p>
            </div>

            <div className="la-field">
              <label htmlFor="verificationCode">Dogrulama Kodu *</label>
              <input
                id="verificationCode"
                name="verificationCode"
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                maxLength={6}
                required
              />
            </div>

            <button type="submit" className="la-submit" disabled={loading}>
              {loading ? 'Doğrulanıyor…' : 'Randevuyu Onayla'}
            </button>

            {verificationResult && <div className="la-alert la-alert-info">{verificationResult}</div>}
          </form>
        )}

        {step === 3 && appointment && (
          <section className="la-success">
            <h3>Randevu Talebiniz Alindi</h3>
            <p>
              {appointment.fullName} için{' '}
              <strong>{new Date(appointment.appointmentDate).toLocaleDateString('tr-TR')}</strong>{' '}
              tarihli randevu isteginiz basariyla kaydedildi.
            </p>
            <p>
              <strong>Saat:</strong>{' '}
              {appointment.slotStartTime && appointment.slotEndTime
                ? `${appointment.slotStartTime} - ${appointment.slotEndTime}`
                : '-'}{' '}
              <strong>Masa:</strong> {appointment.seatNumber}
            </p>
            <p>
              Randevunuzu kesinleştirmek için lütfen e-posta kutunuza gönderilen QR kodunu
              kütüphane personeline taratarak onaylatınız.
            </p>
            <div className="la-alert la-alert-warning la-warning-list">
              <ul>
                <li>Randevuya gidilmediği takdirde 3 gün boyunca yeni bir randevu oluşturulamaz.</li>
                <li>Lütfen randevunuza kimliğiniz veya öğrenci kartınızla geliniz.</li>
              </ul>
            </div>
            {verificationResult && <p className="la-success-message">{verificationResult}</p>}
            <div className="la-qr-card">
              <h4>QR Kodunuz</h4>
              {qrLoading && <p className="la-qr-status">QR kodu yükleniyor…</p>}
              {qrError && !qrLoading && <p className="la-qr-status la-qr-status-error">{qrError}</p>}
              {!qrLoading && !qrError && qrImage && (
                <>
                  <img src={qrImage} alt="Randevu QR kodu" className="la-qr-image" />
                  <p className="la-qr-hint">
                    QR kodunuzu ekran görüntüsü alarak saklayabilir veya aşağıdaki
                    baglanti ile indirebilirsiniz.
                  </p>
                  <div className="la-qr-actions">
                    <a href={qrImage} download={`kutuphane-randevu-qr-${appointment?.seatNumber || 'kod'}.png`}>
                      QR'i indir
                    </a>
                  </div>
                </>
              )}
            </div>
            {appointment.appointmentCode && (
              <div className="la-code-card">
                <span className="la-code-label">Randevu Kodunuz</span>
                <strong className="la-code-value">{appointment.appointmentCode}</strong>
                <span className="la-code-hint">
                  QR ile sorun yasanmasi durumunda bu kodu personele ileterek randevunuzu hizlica onaylatabilirsiniz.
                </span>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default LibraryAppointment;
